/**
 * Error Message Extraction Utility
 * 
 * Standalone utility for extracting error messages from various error formats.
 * This module has minimal dependencies (only constants) to prevent circular dependencies.
 * 
 * Architecture:
 * - Pure function utility (no side effects)
 * - Minimal dependencies: only imports constants from `errorMessages.js` (which has no dependencies)
 * - Can be used anywhere without import cycles
 * - Safe from circular dependencies: constants file cannot create cycles
 * 
 * @module errorExtractor
 */

import { ERROR_MESSAGES } from './errorMessages';

/**
 * Check if a string contains HTML content
 * @param {string} str - String to check
 * @returns {boolean} True if string contains HTML tags
 */
const isHTMLString = (str) => {
  if (typeof str !== 'string') return false;
  // Check for common HTML patterns
  const htmlPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlPattern.test(str) || 
         str.trim().startsWith('<!DOCTYPE') || 
         str.trim().startsWith('<html');
};

/**
 * Check if a string is a JSON string
 * @param {string} str - String to check
 * @returns {boolean} True if string appears to be JSON
 */
const isJSONString = (str) => {
  if (typeof str !== 'string') return false;
  const trimmed = str.trim();
  return (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
         (trimmed.startsWith('[') && trimmed.endsWith(']'));
};

/**
 * Parse JSON string and extract error message
 * @param {string} jsonString - JSON string to parse
 * @param {string} defaultMessage - Default message if parsing fails
 * @returns {string} Extracted message or default message
 */
const extractFromJSONString = (jsonString, defaultMessage) => {
  try {
    const parsed = JSON.parse(jsonString);
    // Recursively extract from parsed JSON using the main extraction function
    return extractErrorMessage(parsed, defaultMessage);
  } catch {
    // If JSON parsing fails, return default message
    return defaultMessage;
  }
};

/**
 * Sanitize HTML string by extracting text content or returning default message
 * @param {string} htmlString - HTML string to sanitize
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string} Sanitized message or default message
 */
const sanitizeHTMLString = (htmlString, defaultMessage) => {
  // Try to extract text from common error page patterns
  // Look for common error message patterns in HTML
  const titleMatch = htmlString.match(/<title[^>]*>([^<]+)<\/title>/i);
  if (titleMatch && titleMatch[1]) {
    const title = titleMatch[1].trim();
    if (title && title.length < 200) { // Reasonable length
      return title;
    }
  }
  
  // Look for error messages in common HTML structures
  const errorPatterns = [
    /<h1[^>]*>([^<]+)<\/h1>/i,
    /<h2[^>]*>([^<]+)<\/h2>/i,
    /<p[^>]*class=["']?error["']?[^>]*>([^<]+)<\/p>/i,
    /<div[^>]*class=["']?error["']?[^>]*>([^<]+)<\/div>/i,
  ];
  
  for (const pattern of errorPatterns) {
    const match = htmlString.match(pattern);
    if (match && match[1]) {
      const text = match[1].trim();
      if (text && text.length < 200) {
        return text;
      }
    }
  }
  
  // If no meaningful text found, return default message
  return defaultMessage;
};

/**
 * Sanitize string to ensure it's not HTML or JSON
 * @param {string} str - String to sanitize
 * @param {string} defaultMessage - Default message if sanitization fails
 * @returns {string} Sanitized message
 */
const sanitizeString = (str, defaultMessage) => {
  if (typeof str !== 'string' || !str.trim()) {
    return defaultMessage;
  }
  
  // Check if it's HTML
  if (isHTMLString(str)) {
    return sanitizeHTMLString(str, defaultMessage);
  }
  
  // Check if it's JSON string
  if (isJSONString(str)) {
    return extractFromJSONString(str, defaultMessage);
  }
  
  // Check if string is too long (likely raw HTML/JSON that wasn't caught)
  if (str.length > 500) {
    return defaultMessage;
  }
  
  // Valid string message
  return str;
};

/**
 * Extract error message from an error object (axios error, Error instance, etc.)
 * Handles network errors, timeouts, and other error types consistently
 * 
 * @param {Error|object} error - Error object (axios error, Error instance, etc.)
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string} Extracted error message
 * 
 * @example
 * // Extract from axios error
 * const message = extractErrorFromObject(error, 'Operation failed');
 * 
 * // Extract from Error instance
 * const message = extractErrorFromObject(new Error('Something went wrong'), 'Default message');
 */
export const extractErrorFromObject = (error, defaultMessage = ERROR_MESSAGES.DEFAULT_ERROR) => {
  // Handle error objects from handleError() - use originalError for proper extraction
  if (error?.originalError) {
    return extractErrorFromObject(error.originalError, defaultMessage);
  }
  
  // Handle axios response errors
  if (error?.response?.data) {
    return extractErrorMessage(error.response.data, defaultMessage);
  }
  
  // Handle network errors (request sent but no response received)
  if (error?.request && !error?.response) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }
  
  // Handle timeout errors
  if (error?.code === 'ECONNABORTED') {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }
  
  // Handle network error codes
  if (error?.code === 'ERR_NETWORK') {
    return ERROR_MESSAGES.NETWORK_ERROR_DETAILED;
  }
  
  // Handle Error instances - sanitize the message
  if (error instanceof Error) {
    if (error.message) {
      return sanitizeString(error.message, defaultMessage);
    }
    return defaultMessage;
  }
  
  // Handle error data objects
  return extractErrorMessage(error, defaultMessage);
};

/**
 * Extract error message from error data (response.data or error object)
 * This is a standalone utility that can be used with any error response format
 * 
 * @param {any} errorData - Error data from API response (error.response.data) or error object
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string} Extracted error message (never returns JSON string or HTML)
 * 
 * @example
 * // Extract from axios error
 * const message = extractErrorMessage(error.response.data, 'Operation failed');
 * 
 * // Extract from error object
 * const message = extractErrorMessage(error, 'An error occurred');
 */
export const extractErrorMessage = (errorData, defaultMessage = ERROR_MESSAGES.DEFAULT_ERROR) => {
  if (!errorData) {
    return defaultMessage;
  }
  
  // If errorData itself is a string, sanitize it first
  if (typeof errorData === 'string') {
    return sanitizeString(errorData, defaultMessage);
  }
  
  // Handle nested error structure: { isSuccess: false, error: { message: "..." } }
  if (errorData.error) {
    if (typeof errorData.error === 'string') {
      return sanitizeString(errorData.error, defaultMessage);
    }
    if (errorData.error.message && typeof errorData.error.message === 'string') {
      return sanitizeString(errorData.error.message, defaultMessage);
    }
    if (errorData.error.detail && typeof errorData.error.detail === 'string') {
      return sanitizeString(errorData.error.detail, defaultMessage);
    }
    // Recursively extract from error object
    const extracted = extractErrorMessage(errorData.error, defaultMessage);
    if (extracted !== defaultMessage) {
      return extracted;
    }
  }
  
  // Handle direct message field
  if (errorData.message) {
    if (typeof errorData.message === 'string') {
      return sanitizeString(errorData.message, defaultMessage);
    }
    // If message is an object, try to extract from it
    if (typeof errorData.message === 'object') {
      if (errorData.message.message && typeof errorData.message.message === 'string') {
        return sanitizeString(errorData.message.message, defaultMessage);
      }
      if (Array.isArray(errorData.message)) {
        const messages = errorData.message
          .map(msg => typeof msg === 'string' ? sanitizeString(msg, '') : '')
          .filter(msg => msg);
        return messages.length > 0 ? messages.join(', ') : defaultMessage;
      }
    }
  }
  
  // Handle detail field (common in Django/DRF responses)
  if (errorData.detail) {
    if (typeof errorData.detail === 'string') {
      return sanitizeString(errorData.detail, defaultMessage);
    }
    if (Array.isArray(errorData.detail)) {
      const details = errorData.detail
        .map(detail => typeof detail === 'string' ? sanitizeString(detail, '') : '')
        .filter(detail => detail);
      return details.length > 0 ? details.join(', ') : defaultMessage;
    }
  }
  
  // Handle non_field_errors (common in Django validation errors)
  if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
    const errors = errorData.non_field_errors
      .map(err => typeof err === 'string' ? sanitizeString(err, '') : '')
      .filter(err => err);
    if (errors.length > 0) {
      return errors.join(', ');
    }
  }
  
  // Handle array of errors
  if (Array.isArray(errorData)) {
    const messages = errorData
      .map(err => {
        if (typeof err === 'string') {
          return sanitizeString(err, '');
        }
        if (err && typeof err === 'object' && err.message) {
          return sanitizeString(err.message, '');
        }
        return '';
      })
      .filter(msg => msg);
    return messages.length > 0 ? messages.join(', ') : defaultMessage;
  }
  
  // Handle Django field errors: { email: ["Enter a valid email address."] }
  // This should be checked before generic object iteration to properly format field errors
  if (typeof errorData === 'object' && !Array.isArray(errorData)) {
    const fieldErrors = [];
    const skipKeys = ['code', 'status', 'statusCode', 'timestamp', 'path', 'type', 'error', 'message', 'detail', 'non_field_errors'];
    
    for (const key in errorData) {
      if (skipKeys.includes(key)) continue;
      
      const value = errorData[key];
      
      // Handle array of error messages for a field: { email: ["Error 1", "Error 2"] }
      if (Array.isArray(value)) {
        const fieldMessages = value
          .map(msg => typeof msg === 'string' ? sanitizeString(msg, '') : '')
          .filter(msg => msg);
        if (fieldMessages.length > 0) {
          // Format as "field: message1, message2"
          const fieldLabel = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          fieldErrors.push(`${fieldLabel}: ${fieldMessages.join(', ')}`);
        }
      }
      // Handle single string error for a field: { email: "Error message" }
      else if (typeof value === 'string' && value.trim()) {
        const sanitized = sanitizeString(value, '');
        if (sanitized && sanitized.trim()) {
          const fieldLabel = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          fieldErrors.push(`${fieldLabel}: ${sanitized}`);
        }
      }
    }
    
    // If we found field errors, return them joined
    if (fieldErrors.length > 0) {
      return fieldErrors.join('. ');
    }
    
    // Fallback: try to find any string value in the error object
    for (const key in errorData) {
      if (typeof errorData[key] === 'string' && errorData[key].trim()) {
        // Skip keys that are likely not user-facing messages
        if (!skipKeys.includes(key)) {
          const sanitized = sanitizeString(errorData[key], defaultMessage);
          // Only return if sanitized is a valid message (not empty, not default)
          if (sanitized && sanitized.trim() && sanitized !== defaultMessage) {
            return sanitized;
          }
        }
      }
    }
  }
  
  // Last resort: return default message (never stringify the whole object)
  return defaultMessage;
};

export default {
  extractErrorMessage,
  extractErrorFromObject
};

